import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Panel, Pill } from "@/components/admin/AdminUI";
import { PERIODS, perWeek } from "@/lib/subs";

function Err({ id, children }) {
  return children ? <p className="cw-error mt-1 text-xs" role="alert" data-testid={id}>{children}</p> : null;
}

function SubRow({ r, err, onChange, mismatch }) {
  const pw = perWeek(r.price, r.period);
  return (
    <tr data-testid={`sub-row-${r.tier}-${r.period}`}>
      <td className="px-2 py-3 align-top text-sm font-medium"><div className="flex h-10 items-center">{PERIODS.find((p) => p.key === r.period).label}</div></td>
      <td className="px-2 py-3 align-top">
        <div className="flex items-center gap-3">
          <div className="relative w-32">
            <span className="cw-muted pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm">$</span>
            <Input value={r.price} onChange={(e) => onChange("price", e.target.value.replace(/[^\d.]/g, ""))} inputMode="decimal" aria-label={`${r.tier} ${r.period} price`}
              aria-invalid={!!err?.price} className={`h-10 rounded-lg bg-white pl-6 tabular-nums ${err?.price ? "border-destructive" : ""}`} data-testid={`sub-price-input-${r.tier}-${r.period}`} />
          </div>
          <span className="cw-muted whitespace-nowrap text-xs tabular-nums" data-testid={`sub-per-week-${r.tier}-${r.period}`}>{pw ? `$${pw}/wk` : "—"}</span>
        </div>
        <Err id={`sub-price-error-${r.tier}-${r.period}`}>{err?.price}</Err>
        {mismatch && <p className="cw-warning mt-1 text-xs" data-testid={`sub-mismatch-${r.tier}-${r.period}`}>Differs from Crushr</p>}
      </td>
      <td className="px-2 py-3 align-top">
        <Input value={r.store_product_id} onChange={(e) => onChange("store_product_id", e.target.value.trim())} placeholder="cw_tier_period" aria-label={`${r.tier} ${r.period} store product ID`}
          aria-invalid={!!err?.store_product_id} className={`h-10 rounded-lg bg-white font-mono text-xs ${err?.store_product_id ? "border-destructive" : ""}`} data-testid={`sub-product-id-input-${r.tier}-${r.period}`} />
        <Err id={`sub-product-id-error-${r.tier}-${r.period}`}>{err?.store_product_id}</Err>
      </td>
      <td className="px-2 py-3 align-top"><div className="flex h-10 items-center">
        <Switch checked={r.active} onCheckedChange={(v) => onChange("active", v)} aria-label={`${r.tier} ${r.period} active`} data-testid={`sub-active-toggle-${r.tier}-${r.period}`} />
      </div></td>
      <td className="px-2 py-3 align-top">
        <Input value={r.save_pct} onChange={(e) => onChange("save_pct", e.target.value.replace(/\D/g, ""))} inputMode="numeric" maxLength={3} placeholder="None"
          aria-label={`${r.tier} ${r.period} save percent`} aria-invalid={!!err?.save_pct}
          className={`h-10 w-20 rounded-lg bg-white tabular-nums ${err?.save_pct ? "border-destructive" : ""}`} data-testid={`sub-save-pct-input-${r.tier}-${r.period}`} />
        <Err id={`sub-save-pct-error-${r.tier}-${r.period}`}>{err?.save_pct}</Err>
      </td>
      <td className="px-2 py-3 align-top">
        <div className="flex min-h-10 flex-wrap items-center gap-1" data-testid={`sub-legacy-${r.tier}-${r.period}`}>
          {r.legacy.length ? r.legacy.map((id) => <Pill key={id} className="font-mono">{id}</Pill>) : <span className="cw-muted text-xs">None</span>}
        </div>
      </td>
    </tr>
  );
}

export default function SubscriptionTable({ tier, rows, errors, onChange, crushrRows }) {
  return (
    <Panel className="overflow-hidden" data-testid={`subs-group-${tier.key}`}>
      <div className="px-6 pb-2 pt-5"><h3 className="font-semibold">{tier.title}</h3></div>
      <div className="overflow-x-auto px-4 pb-3">
        <table className="w-full min-w-[960px] text-left" data-testid={`subs-table-${tier.key}`}>
          <thead><tr className="cw-muted text-xs">
            <th className="w-28 px-2 py-2 font-medium">Period</th><th className="px-2 py-2 font-medium">Price (USD) · per week</th>
            <th className="px-2 py-2 font-medium">Store product ID</th><th className="px-2 py-2 font-medium">Active</th>
            <th className="w-44 px-2 py-2 font-medium">Save %<span className="block font-normal" data-testid={`sub-save-pct-help-${tier.key}`}>{'Shown as a "Save N%" badge on the plan length in the app. Leave empty for no badge.'}</span></th>
            <th className="px-2 py-2 font-medium">Legacy IDs</th>
          </tr></thead>
          <tbody className="divide-y divide-border">
            {rows.map((r) => {
              const twin = crushrRows?.find((c) => c.period === r.period);
              const mismatch = tier.key === "winkr" && twin && twin.price && r.price && +twin.price !== +r.price;
              return <SubRow key={r.k} r={r} err={errors[r.k]} mismatch={mismatch} onChange={(f, v) => onChange(r.k, f, v)} />;
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
